import React from "react";
import { AlertTriangle, RotateCcw, Home } from "lucide-react";

/**
 * React error boundary used to wrap route-level pages (currently the
 * Workspace). When a descendant throws during render / lifecycle the
 * boundary swaps the tree for a readable error card so the app never
 * drops to a black screen.
 *
 * Design notes:
 * - Class component is required — hooks cannot catch render errors.
 * - We surface the error stack in <details> so the user can paste it
 *   into a support message. On development/preview builds React also
 *   prints its overlay which is fine; this card is the production UX.
 * - "Reload" does a soft reload (back to the route so any transient
 *   state like a deleted project is re-fetched). "Back to home" routes
 *   the user out of the broken view entirely.
 */
export default class RouteErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null, info: null };
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        this.setState({ info });
        try {
            // eslint-disable-next-line no-console
            console.error("RouteErrorBoundary caught:", error, info);
        } catch {
            /* ignore */
        }
        // Fire-and-forget: ship the error to the backend so superadmin
        // can triage field crashes from the admin panel. Don't block
        // or re-throw on network failure — the UX card still renders.
        try {
            const backend =
                (typeof process !== "undefined" &&
                    process.env &&
                    process.env.REACT_APP_BACKEND_URL) ||
                "";
            const body = JSON.stringify({
                message: (error && (error.message || String(error))) || "",
                stack: (error && error.stack) || "",
                component_stack: (info && info.componentStack) || "",
                url:
                    typeof window !== "undefined"
                        ? window.location.href
                        : "",
                user_agent:
                    typeof navigator !== "undefined"
                        ? navigator.userAgent
                        : "",
                label: this.props.label || "",
            });
            let token = "";
            try {
                token =
                    (typeof localStorage !== "undefined" &&
                        localStorage.getItem("planflow.auth_token")) ||
                    "";
            } catch {
                /* ignore */
            }
            fetch(`${backend}/api/client-errors`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(token
                        ? { Authorization: `Bearer ${token}` }
                        : {}),
                },
                body,
                keepalive: true,
            }).catch(() => {
                /* ignore */
            });
        } catch {
            /* ignore */
        }
    }

    handleReload = () => {
        try {
            window.location.reload();
        } catch {
            /* ignore */
        }
    };

    handleHome = () => {
        try {
            window.location.href = "/home";
        } catch {
            /* ignore */
        }
    };

    render() {
        if (!this.state.error) return this.props.children;

        const label = this.props.label || "page";
        const err = this.state.error;
        const stack =
            (err && (err.stack || err.message)) || "No stack trace available.";

        return (
            <div
                data-testid="route-error-boundary"
                className="min-h-[100dvh] w-full flex items-center justify-center p-6 bg-[#0A1128] text-slate-100"
            >
                <div className="w-full max-w-xl bg-[#131A2F] border border-rose-500/40 rounded-sm p-5 shadow-xl">
                    <div className="flex items-start gap-3">
                        <div className="shrink-0 w-10 h-10 rounded-full bg-rose-500/15 flex items-center justify-center">
                            <AlertTriangle className="w-5 h-5 text-rose-300" />
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-white font-bold text-lg">
                                Something went wrong loading this {label}
                            </h2>
                            <p className="text-slate-400 text-sm mt-1">
                                The error below has been logged to your
                                browser console. Try reloading — if it
                                keeps happening, copy the stack trace and
                                send it along so we can fix it.
                            </p>
                        </div>
                    </div>

                    <details
                        data-testid="route-error-details"
                        className="mt-4 bg-[#0A1128] border border-[#2A3655] rounded-sm p-3 text-[11px] leading-relaxed"
                    >
                        <summary className="cursor-pointer text-rose-300 font-semibold uppercase tracking-wider text-[10px]">
                            Stack trace
                        </summary>
                        <pre className="mt-2 whitespace-pre-wrap break-words font-mono text-slate-300">
                            {stack}
                        </pre>
                    </details>

                    <div className="mt-4 flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={this.handleReload}
                            data-testid="route-error-reload"
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-sm bg-[#007AFF] hover:bg-[#3395FF] text-white font-bold text-sm transition-colors"
                        >
                            <RotateCcw className="w-4 h-4" />
                            Reload
                        </button>
                        <button
                            type="button"
                            onClick={this.handleHome}
                            data-testid="route-error-home"
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-sm bg-[#1C253C] border border-[#2A3655] hover:bg-[#2A3655] text-slate-100 font-bold text-sm transition-colors"
                        >
                            <Home className="w-4 h-4" />
                            Back to home
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}
